import React, { useEffect, useState, useCallback } from "react";
import "../orderList/orderList.css";
import { userRequest } from "../../requestMethods";
import { useSelector } from "react-redux";
import moment from "moment";
import Select from "react-select";
import { commonSelectProps } from "../../components/shared/SelectStyles";
import {
    Close,
    CheckCircle,
    Replay,
    Cancel as CancelIcon,
} from "@material-ui/icons";

const statusOptions = [
    { label: "All", value: "" },
    { label: "Pending", value: "pending" },
    { label: "Resolved", value: "resolved" },
    { label: "Rejected", value: "rejected" },
];

const typeOptions = [
    { label: "All", value: "" },
    { label: "New Order", value: "createCase" },
    { label: "Order Updated", value: "details" },
    { label: "Cancel Order", value: "status" },
];

const Logs = () => {
    const currentUser = useSelector((state) => state.user.currentUser);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedLog, setSelectedLog] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [editRemarks, setEditRemarks] = useState("");

    const [filters, setFilters] = useState({
        status: "",
        type: "",
        dateFrom: "",
        dateTo: "",
    });

    const outright = currentUser?.type === "outright";
    const manager = currentUser?.type === "manager";

    const canView = outright || manager;

    const getPrincipleParam = useCallback(() => {
        const p = currentUser?.principle;
        if (p != null && p !== "") {
            if (typeof p === "object" && p._id) return p._id;
            return p;
        }
        return currentUser?._id;
    }, [currentUser?.principle, currentUser?._id]);

    const fetchLogs = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            const principleParam = getPrincipleParam();
            if (principleParam) params.append("principle", principleParam);
            if (filters.status) params.append("status", filters.status);
            if (filters.type) params.append("type", filters.type);
            if (filters.dateFrom) params.append("dateFrom", filters.dateFrom);
            if (filters.dateTo) params.append("dateTo", filters.dateTo);

            const query = params.toString();
            const url = query ? `/logs?${query}` : `/logs`;

            const res = await userRequest.get(url);
            setLogs(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error("Error fetching logs:", err);
            alert("Failed to fetch logs");
        } finally {
            setLoading(false);
        }
    }, [filters, getPrincipleParam]);

    useEffect(() => {
        if (canView) {
            fetchLogs();
        }
    }, [canView, fetchLogs]);

    const openModal = (log) => {
        setSelectedLog(log);
        setEditRemarks(log?.remarks || "");
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedLog(null);
        setEditRemarks("");
    };

    const handleStatusChange = async (logId, nextStatus) => {
        if (!logId) return;
        let confirmMessage;
        if (nextStatus === "resolved") {
            confirmMessage = "Mark this log as resolved?";
        } else if (nextStatus === "rejected") {
            confirmMessage = "Reject this log?";
        } else {
            confirmMessage = "Reopen this log (mark as pending)?";
        }
        if (!window.confirm(confirmMessage)) return;

        setUpdatingStatus(true);
        try {
            const payload = {
                status: nextStatus,
                remarks: editRemarks,
            };
            if (nextStatus === "resolved") {
                payload.resolvedBy = currentUser?._id;
            } else if (nextStatus === "rejected") {
                payload.rejectedBy = currentUser?._id;
            }

            const res = await userRequest.put(`/logs/status/${logId}`, payload);

            const updatedLog = res.data?.log;
            setLogs((prev) =>
                prev.map((l) => (l._id === logId ? updatedLog || l : l))
            );
            if (selectedLog?._id === logId && updatedLog) {
                setSelectedLog(updatedLog);
            }
            alert(res.data?.message || "Log updated.");
        } catch (err) {
            console.error("Error updating log status:", err);
            alert(err?.response?.data?.message || "Failed to update log.");
        } finally {
            setUpdatingStatus(false);
        }
    };

    const getStatusBadge = (status) => {
        const normalized = String(status || "pending").toLowerCase();
        if (normalized === "resolved") {
            return (
                <span className="px-2 py-1 rounded-full text-[10px] font-semibold bg-green-200 text-green-800">
                    Resolved
                </span>
            );
        }
        if (normalized === "rejected") {
            return (
                <span className="px-2 py-1 rounded-full text-[10px] font-semibold bg-red-200 text-red-900">
                    Rejected
                </span>
            );
        }
        return (
            <span className="px-2 py-1 rounded-full text-[10px] font-semibold bg-yellow-200 text-yellow-800">
                Pending
            </span>
        );
    };

    const getTypeBadge = (type) => {
        const normalized = String(type || "").toLowerCase();
        if (normalized === "createcase") {
            return (
                <span className="px-2 py-1 rounded-full text-[10px] font-semibold bg-green-200 text-green-800">
                    New Order
                </span>
            );
        }
        if (normalized === "status") {
            return (
                <span className="px-2 py-1 rounded-full text-[10px] font-semibold bg-red-200 text-red-800">
                    Cancel Order
                </span>
            );
        }
        if (normalized === "details") {
            return (
                <span className="px-2 py-1 rounded-full text-[10px] font-semibold bg-gray-200 text-gray-800">
                    Order Updated
                </span>
            );
        }
        return (
            <span className="px-2 py-1 rounded-full text-[10px] font-semibold bg-gray-200 text-gray-800">
                {type || "Unknown"}
            </span>
        );
    };

    if (!canView) {
        return (
            <div className="container mx-auto px-2 sm:px-4 py-8 block">
                <div className="bg-white rounded-lg shadow p-8 text-center">
                    <h2 className="text-2xl font-bold text-red-600 mb-4">
                        Access Denied
                    </h2>
                    <p className="text-gray-700">
                        Only Outright and Manager users can access this page.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-2 sm:px-4 py-8 block">
            {/* Filter Panel */}
            <div className="bg-white rounded-lg shadow p-2 sm:p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 sm:gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Date From
                        </label>
                        <input
                            type="date"
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            value={filters.dateFrom}
                            onChange={(e) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    dateFrom: e.target.value,
                                }))
                            }
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Date To
                        </label>
                        <input
                            type="date"
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            value={filters.dateTo}
                            onChange={(e) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    dateTo: e.target.value,
                                }))
                            }
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Status
                        </label>
                        <Select
                            options={statusOptions}
                            value={
                                statusOptions.find(
                                    (opt) => opt.value === filters.status
                                ) || statusOptions[0]
                            }
                            onChange={(e) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    status: e?.value || "",
                                }))
                            }
                            placeholder="Status"
                            {...commonSelectProps}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Type
                        </label>
                        <Select
                            options={typeOptions}
                            value={
                                typeOptions.find(
                                    (opt) => opt.value === filters.type
                                ) || typeOptions[0]
                            }
                            onChange={(e) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    type: e?.value || "",
                                }))
                            }
                            placeholder="Type"
                            {...commonSelectProps}
                        />
                    </div>
                </div>
            </div>

            {/* Data Table */}
            <div className="bg-white rounded-lg shadow overflow-x-auto orders-table-wrapper">
                <table className="min-w-[1200px] divide-y divide-gray-200 orders-table w-full">
                    <thead className="bg-orange-400">
                        <tr>
                            <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-black uppercase tracking-wider">
                                Invoice ID
                            </th>
                            <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-black uppercase tracking-wider">
                                CO Number
                            </th>
                            <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-black uppercase tracking-wider">
                                Customer
                            </th>
                            <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-black uppercase tracking-wider">
                                Type
                            </th>
                            <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-black uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-black uppercase tracking-wider">
                                Created By
                            </th>
                            <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-black uppercase tracking-wider">
                                Created At
                            </th>
                            <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-black uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {loading ? (
                            <tr>
                                <td
                                    colSpan="8"
                                    className="px-2 sm:px-6 py-4 text-center text-gray-500"
                                >
                                    Loading...
                                </td>
                            </tr>
                        ) : logs.length === 0 ? (
                            <tr>
                                <td
                                    colSpan="8"
                                    className="px-2 sm:px-6 py-4 text-center text-gray-500"
                                >
                                    No logs found.
                                </td>
                            </tr>
                        ) : (
                            logs.map((log) => {
                                const st = String(
                                    log.status || "pending"
                                ).toLowerCase();
                                const isPending = st === "pending";
                                const isRejected = st === "rejected";
                                const rowBg = isPending
                                    ? "bg-yellow-50 hover:bg-yellow-100"
                                    : isRejected
                                    ? "bg-red-50 hover:bg-red-100"
                                    : "bg-green-50 hover:bg-green-100";
                                return (
                                    <tr
                                        key={log._id}
                                        className={`${rowBg} transition-colors duration-200 cursor-pointer`}
                                        onClick={() => openModal(log)}
                                    >
                                        <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-[11px] sm:text-sm text-gray-900">
                                            {log.order?.invoiceId || "N/A"}
                                        </td>
                                        <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-[11px] sm:text-sm text-gray-900">
                                            {log.order?.coNumber || "N/A"}
                                        </td>
                                        <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-[11px] sm:text-sm text-gray-900">
                                            {log.customer?.name ||
                                                log.order?.userId?.name ||
                                                "N/A"}
                                        </td>
                                        <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-[11px] sm:text-sm">
                                            {getTypeBadge(log.type)}
                                        </td>
                                        <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-[11px] sm:text-sm">
                                            {getStatusBadge(log.status)}
                                        </td>
                                        <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-[11px] sm:text-sm text-gray-900">
                                            {log.createdBy?.name || "Unknown"}
                                        </td>
                                        <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-[11px] sm:text-sm text-gray-900">
                                            {moment(log.createdAt).format(
                                                "DD/MM/YYYY HH:mm"
                                            )}
                                        </td>
                                        <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-[11px] sm:text-sm">
                                            <div className="flex items-center gap-2">
                                                {isPending ? (
                                                    <>
                                                        <CheckCircle
                                                            className="pointer icon text-green-600 hover:text-green-800"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleStatusChange(
                                                                    log._id,
                                                                    "resolved"
                                                                );
                                                            }}
                                                            titleAccess="Mark as Resolved"
                                                        />
                                                        <CancelIcon
                                                            className="pointer icon text-red-600 hover:text-red-800"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleStatusChange(
                                                                    log._id,
                                                                    "rejected"
                                                                );
                                                            }}
                                                            titleAccess="Reject Log"
                                                        />
                                                    </>
                                                ) : (
                                                    <Replay
                                                        className="pointer icon text-yellow-600 hover:text-yellow-800"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleStatusChange(
                                                                log._id,
                                                                "pending"
                                                            );
                                                        }}
                                                        titleAccess="Reopen (Mark Pending)"
                                                    />
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Detail Modal */}
            {showModal && selectedLog && (
                <div className="overlayContainer installment-modal">
                    <div className="center installment-modal__dialog">
                        <div className="close" onClick={closeModal}>
                            <Close />
                        </div>
                        <div className="actionContainer">
                            <div className="updateOrder">
                                <h3 className="updateProductTitle text-xl font-semibold mb-4">
                                    Log Details
                                </h3>
                                <div className="flex flex-col gap-4 mb-4">
                                    <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                        <span className="font-medium text-gray-700">
                                            Status:
                                        </span>
                                        <span>
                                            {getStatusBadge(selectedLog.status)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                        <span className="font-medium text-gray-700">
                                            Type:
                                        </span>
                                        <span>
                                            {getTypeBadge(selectedLog.type)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                        <span className="font-medium text-gray-700">
                                            Created At:
                                        </span>
                                        <span className="text-gray-900">
                                            {moment(
                                                selectedLog.createdAt
                                            ).format("DD/MM/YYYY HH:mm:ss")}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                        <span className="font-medium text-gray-700">
                                            Invoice ID:
                                        </span>
                                        <span className="text-gray-900">
                                            {selectedLog.order?.invoiceId ||
                                                "N/A"}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                        <span className="font-medium text-gray-700">
                                            CO Number:
                                        </span>
                                        <span className="text-gray-900">
                                            {selectedLog.order?.coNumber ||
                                                "N/A"}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                        <span className="font-medium text-gray-700">
                                            Customer:
                                        </span>
                                        <span className="text-gray-900">
                                            {selectedLog.customer?.name ||
                                                selectedLog.order?.userId?.name ||
                                                "N/A"}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                        <span className="font-medium text-gray-700">
                                            Created By:
                                        </span>
                                        <span className="text-gray-900">
                                            {selectedLog.createdBy?.name ||
                                                "Unknown"}
                                        </span>
                                    </div>
                                    {selectedLog.status === "resolved" && (
                                        <>
                                            <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                                <span className="font-medium text-gray-700">
                                                    Resolved By:
                                                </span>
                                                <span className="text-gray-900">
                                                    {selectedLog.resolvedBy
                                                        ?.name || "Unknown"}
                                                </span>
                                            </div>
                                            <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                                <span className="font-medium text-gray-700">
                                                    Resolved At:
                                                </span>
                                                <span className="text-gray-900">
                                                    {selectedLog.resolvedAt
                                                        ? moment(
                                                              selectedLog.resolvedAt
                                                          ).format(
                                                              "DD/MM/YYYY HH:mm:ss"
                                                          )
                                                        : "—"}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                    {selectedLog.status === "rejected" && (
                                        <>
                                            <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                                <span className="font-medium text-gray-700">
                                                    Rejected By:
                                                </span>
                                                <span className="text-gray-900">
                                                    {selectedLog.rejectedBy
                                                        ?.name || "Unknown"}
                                                </span>
                                            </div>
                                            <div className="flex justify-between items-center py-2 border-b installment-detail-row">
                                                <span className="font-medium text-gray-700">
                                                    Rejected At:
                                                </span>
                                                <span className="text-gray-900">
                                                    {selectedLog.rejectedAt
                                                        ? moment(
                                                              selectedLog.rejectedAt
                                                          ).format(
                                                              "DD/MM/YYYY HH:mm:ss"
                                                          )
                                                        : "—"}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                    <div className="py-2 border-b installment-detail-row">
                                        <label className="block font-medium text-gray-700 mb-1">
                                            Remarks
                                        </label>
                                        <textarea
                                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                                            rows={3}
                                            value={editRemarks}
                                            onChange={(e) =>
                                                setEditRemarks(e.target.value)
                                            }
                                            placeholder="Add remarks for this log..."
                                        />
                                    </div>
                                </div>

                                <div className="buttonContainer">
                                    {selectedLog.status === "pending" ? (
                                        <>
                                            <button
                                                className="confirm pointer green"
                                                disabled={updatingStatus}
                                                onClick={() =>
                                                    handleStatusChange(
                                                        selectedLog._id,
                                                        "resolved"
                                                    )
                                                }
                                            >
                                                {updatingStatus
                                                    ? "Updating..."
                                                    : "Mark as Resolved"}
                                            </button>
                                            <button
                                                className="confirm pointer red"
                                                disabled={updatingStatus}
                                                onClick={() =>
                                                    handleStatusChange(
                                                        selectedLog._id,
                                                        "rejected"
                                                    )
                                                }
                                            >
                                                {updatingStatus
                                                    ? "Updating..."
                                                    : "Reject Log"}
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            className="confirm pointer"
                                            disabled={updatingStatus}
                                            style={{
                                                background: "#f59e0b",
                                                color: "white",
                                            }}
                                            onClick={() =>
                                                handleStatusChange(
                                                    selectedLog._id,
                                                    "pending"
                                                )
                                            }
                                        >
                                            {updatingStatus
                                                ? "Updating..."
                                                : "Reopen (Pending)"}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Logs;
